/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserProfile, Course, Quiz, TestResult, Notification, UserRole, Badge, Certificate, ErrorReport, ErrorCategory } from '../types';
import { MOCK_COURSES, MOCK_QUIZZES, MOCK_BADGES } from '../data/mockData';
import { getOrCreateVisitorId } from '../utils/visitorTracker';

interface AppContextProps {
  isPremiumIntelligenceEnabled: boolean;
  setPremiumIntelligenceEnabled: (val: boolean) => void;
  user: UserProfile | null;
  setUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  activePage: string;
  activeCourseId: string | null;
  activeLessonId: string | null;
  activeQuizId: string | null;
  lastResult: TestResult | null;
  selectedBlogId: string | null;
  courses: Course[];
  quizzes: Quiz[];
  notifications: Notification[];
  theme: 'light' | 'dark';
  profileTab: 'view' | 'settings';
  setProfileTab: (tab: 'view' | 'settings') => void;
  pdfLibraryClassId: string | null;
  pdfLibrarySubjectId: string | null;
  pdfLibraryClassName: string | null;
  pdfLibrarySubjectName: string | null;
  setPage: (page: string, params?: { courseId?: string; lessonId?: string; quizId?: string; blogId?: string; profileTab?: 'view' | 'settings'; pdfClassId?: string; pdfSubjectId?: string; pdfClassName?: string; pdfSubjectName?: string }) => void;
  login: (email: string, role: UserRole, password?: string, captcha?: string, expectedCaptcha?: string, forceLogin?: boolean) => Promise<any>;
  loginWithGoogleData: (data: any) => Promise<boolean>;
  signup: (displayName: string, email: string, role: UserRole, currentClass?: string, password?: string, phone?: string, captcha?: string, expectedCaptcha?: string, userState?: string) => Promise<boolean>;
  logout: () => void;
  enrollInCourse: (courseId: string) => void;
  completeLesson: (courseId: string, lessonId: string) => void;
  submitQuizScore: (quizId: string, score: number, answers: any[], timeTakenSeconds: number) => TestResult;
  addNotification: (title: string, message: string, type: 'streak' | 'badge' | 'announcement' | 'reminder') => void;
  clearNotification: (id: string) => void;
  purchaseItem: (cost: number, itemName: string, itemType: 'streak-freeze' | 'avatar' | 'premium') => boolean;
  changeTheme: (theme: 'light' | 'dark') => void;
  authModalOpen: boolean;
  authModalMode: 'login' | 'signup';
  setAuthModalOpen: (open: boolean, mode?: 'login' | 'signup') => void;
  setAuthModalMode: (mode: 'login' | 'signup') => void;
  adminLoginModalOpen: boolean;
  setAdminLoginModalOpen: (open: boolean) => void;
  dashboardTab: 'overview' | 'courses' | 'rewards' | 'premium-intelligence' | 'my-learning' | 'parent-report' | 'purchased-pdfs' | 'security' | 'academic-history';
  setDashboardTab: (tab: 'overview' | 'courses' | 'rewards' | 'premium-intelligence' | 'my-learning' | 'parent-report' | 'purchased-pdfs' | 'security' | 'academic-history') => void;
  adminWorkspace: 'console' | 'studymaterials' | 'analytics' | 'business_analytics' | 'reset' | 'security' | 'admin_management' | 'cms' | 'practice_settings' | 'admission' | 'purchase_reports';
  setAdminWorkspace: (workspace: 'console' | 'studymaterials' | 'analytics' | 'business_analytics' | 'reset' | 'security' | 'admin_management' | 'cms' | 'practice_settings' | 'admission' | 'purchase_reports') => void;
  // Admin/Teacher operations
  createCourse: (course: Omit<Course, 'id'>) => void;
  updateCourse: (id: string, course: Partial<Course>) => void;
  deleteCourse: (id: string) => void;
  createQuiz: (quiz: Omit<Quiz, 'id'>) => void;
  updateQuiz: (id: string, quiz: Partial<Quiz>) => void;
  deleteQuiz: (id: string) => void;
  testResults: TestResult[];
  badges: Badge[];
  certificates: Certificate[];
  errorReports: ErrorReport[];
  reportError: (report: Omit<ErrorReport, 'id' | 'date' | 'status'>) => void;
  resolveErrorReport: (id: string, adminNotes?: string) => void;
  updateQuestionContent: (quizId: string, questionId: string, text: string, options: string[], explanation: string) => void;
  updateLessonNotes: (courseId: string, lessonId: string, notes: string) => void;
  updateUserProfile: (fields: Partial<UserProfile>) => void;
  syncUserProfile: () => Promise<void>;
  isProfileIncomplete: (u?: any) => boolean;
  // Shopping Cart States & Actions
  cart: any[];
  addToCart: (courseId: string) => void;
  addPdfToCart: (chapter: any, price: number, className: string, subjectName: string) => void;
  removeFromCart: (courseId: string) => void;
  clearCart: () => void;
  cartModalOpen: boolean;
  setCartModalOpen: (open: boolean) => void;
  cartModalItem: any | null;
  // Promotions & Dynamic Pricing
  promotions: {
    free_premium_active: string;
    free_premium_title: string;
    free_premium_description: string;
    free_premium_start_date: string;
    free_premium_end_date: string;
    show_homepage_badge: string;
    homepage_badge_title: string;
    homepage_badge_subtitle: string;
    homepage_badge_cta_text: string;
    homepage_badge_cta_destination: string;
    homepage_badge_end_date: string;
    show_offer_image: string;
    offer_image_url: string;
    promo_banner_active?: string;
    promo_banner_slogan?: string;
    promo_banner_code?: string;
    promo_banner_badge?: string;
    membership_plans: any[];
  } | null;
  refreshPromotions: () => Promise<void>;
  activateFreePremiumOnServer: (courseId: string) => Promise<boolean>;
  pendingPurchaseAction: { fn: () => void } | null;
  setPendingPurchaseAction: React.Dispatch<React.SetStateAction<{ fn: () => void } | null>>;
  purchaseAuthMessage: string | null;
  setPurchaseAuthMessage: React.Dispatch<React.SetStateAction<string | null>>;
  guardPurchase: (action: () => void, message?: string, defaultAuthMode?: 'login' | 'signup') => boolean;
  executePendingPurchaseAction: () => void;
  promoteStudent: () => Promise<any>;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Load initial states from LocalStorage or use beautiful defaults
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = localStorage.getItem('mcq_pulse_theme');
    return (stored as 'light' | 'dark') || 'light';
  });

  const [user, setUserState] = useState<UserProfile | null>(() => {
    const stored = localStorage.getItem('mcq_pulse_user');
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      if (parsed) {
        if (!parsed.displayName && parsed.display_name) {
          parsed.displayName = parsed.display_name;
        }
        if (!parsed.uid && parsed.id) {
          parsed.uid = parsed.id;
        }
        if (parsed.role) {
          const rawRoleLower = parsed.role.toLowerCase();
          if (rawRoleLower === 'super admin' || rawRoleLower === 'admin') {
            parsed.role = 'admin';
            if (rawRoleLower === 'super admin') {
              parsed.adminRole = 'Super Admin';
            }
          }
        }
      }
      return parsed;
    } catch (e) {
      return null;
    }
  });

  const setUser = React.useCallback((val: UserProfile | null | ((prev: UserProfile | null) => UserProfile | null)) => {
    setUserState(prev => {
      const next = typeof val === 'function' ? val(prev) : val;
      if (next) {
        if (!next.displayName && (next as any).display_name) {
          next.displayName = (next as any).display_name;
        }
        if (!next.uid && (next as any).id) {
          next.uid = (next as any).id;
        }
        if (next.role) {
          const rawRoleLower = next.role.toLowerCase();
          if (rawRoleLower === 'super admin' || rawRoleLower === 'admin') {
            next.role = 'admin';
            if (rawRoleLower === 'super admin') {
              next.adminRole = 'Super Admin';
            }
          }
        }
      }
      return next;
    });
  }, []);

  const [isProfileSyncing, setIsProfileSyncing] = useState<boolean>(true);

  // STARTUP PROFILE SYNCHRONIZATION PROCESS
  const syncUserProfile = async () => {
    const token = localStorage.getItem('mcq_pulse_token') || localStorage.getItem('token');
    const storedUserRaw = localStorage.getItem('mcq_pulse_user');

    let hydratedClass = 'None';
    if (storedUserRaw) {
      try {
        const parsed = JSON.parse(storedUserRaw);
        hydratedClass = parsed?.currentClass || 'None';
      } catch (e) {
        hydratedClass = 'Error Parsing';
      }
    }

    console.log('[Sync] Hydrated localStorage currentClass:', hydratedClass);

    if (!token) {
      setIsProfileSyncing(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user) {
          const rawRoleLower = (data.user.role?.toLowerCase() || 'student');
          const apiRole: UserRole = (rawRoleLower === 'super admin' || rawRoleLower === 'admin') ? 'admin' : (rawRoleLower as UserRole);
          const adminRoleVal = rawRoleLower === 'super admin' ? 'Super Admin' : data.user.adminRole;
          const apiClass = data.profile?.currentClass || data.profile?.grade_or_class || undefined;

          console.log('[Sync] API returned currentClass:', apiClass);

          const syncedUser: UserProfile = {
            uid: 'user_pulse_' + data.user.id,
            email: data.user.email,
            displayName: data.user.display_name || '',
            role: apiRole,
            adminRole: adminRoleVal,
            phone: data.user.phone || '',
            state: data.user.state || undefined,
            dailyStreak: data.profile?.current_streak !== undefined ? Number(data.profile.current_streak) : 0,
            currentStreak: data.profile?.current_streak !== undefined ? Number(data.profile.current_streak) : 0,
            highestStreak: data.profile?.highest_streak !== undefined ? Number(data.profile.highest_streak) : 0,
            lastPracticeDate: data.profile?.last_practice_date || undefined,
            milestonesUnlocked: data.profile?.milestones_unlocked || [],
            lastActiveDate: data.profile?.last_active_date || new Date().toISOString().split('T')[0],
            xp: data.profile?.xp !== undefined ? Number(data.profile.xp) : 0,
            credits: data.profile?.credits !== undefined ? Number(data.profile.credits) : 0,
            badges: (data.profile?.badges && Array.isArray(data.profile.badges)) ? data.profile.badges : [],
            unlockedCertificates: [],
            isPremium: data.profile?.isPremium !== undefined 
              ? Boolean(data.profile.isPremium) 
              : (data.user.isPremium !== undefined ? Boolean(data.user.isPremium) : false),
            enrolledCourses: (data.profile?.enrolledCourses && Array.isArray(data.profile.enrolledCourses))
              ? data.profile.enrolledCourses
              : [],
            completedQuizzes: [],
            xpHistory: [
              { date: '2026-07-03', xp: 100 },
              { date: '2026-07-04', xp: 200 },
              { date: '2026-07-05', xp: 350 },
              { date: '2026-07-06', xp: 450 }
            ],
            creditsSpent: 0,
            createdAt: new Date().toLocaleDateString(),
            currentClass: apiClass,
            currentAcademicClass: data.profile?.currentAcademicClass || apiClass,
            activeEnrollments: data.profile?.activeEnrollments || [],
            expiredEnrollments: data.profile?.expiredEnrollments || [],
            academicHistory: data.profile?.academicHistory || [],
            academicYear: apiRole === 'student' ? '2026-2027' : undefined,
            photoURL: data.profile?.avatar || ''
          };

          // Database/API profile is the source of truth
          setUser(syncedUser);
          localStorage.setItem('mcq_pulse_user', JSON.stringify(syncedUser));

          console.log('[Sync] Final user.currentClass after synchronization:', syncedUser.currentClass);
        }
      } else if (response.status === 401 || response.status === 403) {
        console.warn('[Sync] Session invalid or expired on server. Invalidation of local cache.');
        localStorage.removeItem('token');
        localStorage.removeItem('mcq_pulse_token');
        localStorage.removeItem('mcq_pulse_user');
        setUser(null);
      }
    } catch (err) {
      console.error('[Sync] Profile synchronization error:', err);
    } finally {
      setIsProfileSyncing(false);
    }
  };

  useEffect(() => {
    syncUserProfile();
  }, [setUser]);

  const [activePage, setActivePage] = useState<string>('home');
  const [activeCourseId, setActiveCourseId] = useState<string | null>(null);
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);
  const [activeQuizId, setActiveQuizId] = useState<string | null>(null);
  const [selectedBlogId, setSelectedBlogId] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<TestResult | null>(null);
  const [profileTab, setProfileTab] = useState<'view' | 'settings'>('view');

  const [pdfLibraryClassId, setPdfLibraryClassId] = useState<string | null>(null);
  const [pdfLibrarySubjectId, setPdfLibrarySubjectId] = useState<string | null>(null);
  const [pdfLibraryClassName, setPdfLibraryClassName] = useState<string | null>(null);
  const [pdfLibrarySubjectName, setPdfLibrarySubjectName] = useState<string | null>(null);

  const [promotions, setPromotions] = useState<any>(null);

  const refreshPromotions = async () => {
    try {
      const res = await fetch('/api/promotions/settings');
      if (res.ok) {
        const data = await res.json();
        setPromotions(data);
      }
    } catch (err) {
      console.error('Failed to fetch promotions settings:', err);
    }
  };

  useEffect(() => {
    refreshPromotions();
  }, []);

  const activateFreePremiumOnServer = async (courseId: string) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('mcq_pulse_token') || '';
      const res = await fetch('/api/promotions/activate-free', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ courseId }),
      });
      const data = await res.json();
      if (!res.ok) {
        addNotification(
          'Activation Failed',
          data.error || 'Failed to activate launch offer.',
          'reminder'
        );
        return false;
      }
      // Success! Update locally enrolled courses list
      if (user) {
        const enrolled = [...(user.enrolledCourses || [])];
        if (!enrolled.includes(courseId)) {
          enrolled.push(courseId);
        }
        updateUserProfile({ enrolledCourses: enrolled });
      }
      return true;
    } catch (err: any) {
      console.error('Error activating free launch offer:', err);
      addNotification(
        'Activation Error',
        'Could not reach the server to activate launch offer.',
        'reminder'
      );
      return false;
    }
  };

  const promoteStudent = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('mcq_pulse_token') || '';
      const res = await fetch('/api/student/promote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.message || 'Promotion failed.');
      }
      if (data.profile) {
        setUserState(prev => {
          if (!prev) return null;
          const updated = {
            ...prev,
            currentClass: data.profile.currentAcademicClass || data.profile.currentClass,
            currentAcademicClass: data.profile.currentAcademicClass || data.profile.currentClass,
            academicHistory: data.profile.academicHistory || prev.academicHistory
          };
          localStorage.setItem('mcq_pulse_user', JSON.stringify(updated));
          return updated;
        });
      }
      addNotification(
        'Academic Promotion',
        data.message || 'Congratulations! You have been promoted to the next class.',
        'badge'
      );
      return data;
    } catch (err: any) {
      console.error('Error promoting student:', err);
      addNotification('Promotion Error', err.message || 'Could not complete promotion.', 'reminder');
      throw err;
    }
  };

  const [cart, setCart] = useState<any[]>(() => {
    try {
      const storedUser = localStorage.getItem('mcq_pulse_user');
      if (!storedUser) return [];
      const parsedUser = JSON.parse(storedUser);
      if (!parsedUser || parsedUser.role !== 'student') return [];
      const userCartKey = `mcq_pulse_cart_${parsedUser.uid || parsedUser.email || 'student'}`;
      const stored = localStorage.getItem(userCartKey) || localStorage.getItem('mcq_pulse_cart');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [cartModalItem, setCartModalItem] = useState<any | null>(null);
  const [authModalOpen, setAuthModalOpenState] = useState(false);
  const [authModalMode, setAuthModalModeState] = useState<'login' | 'signup'>('login');

  const setAuthModalMode = (mode: 'login' | 'signup') => {
    setAuthModalModeState(mode);
    try {
      localStorage.setItem('auth_modal_initial_mode', mode);
    } catch (_) {}
  };

  const setAuthModalOpen = (open: boolean, mode: 'login' | 'signup' = 'login') => {
    if (open) {
      setAuthModalModeState(mode);
      try {
        localStorage.setItem('auth_modal_initial_mode', mode);
      } catch (_) {}
    }
    setAuthModalOpenState(open);
    if (!open) {
      setPurchaseAuthMessage(null);
      setAuthModalModeState('login');
      try {
        localStorage.removeItem('auth_modal_initial_mode');
      } catch (_) {}
    }
  };
  const [adminLoginModalOpen, setAdminLoginModalOpen] = useState(false);
  const [pendingPurchaseAction, setPendingPurchaseAction] = useState<{ fn: () => void } | null>(null);
  const [purchaseAuthMessage, setPurchaseAuthMessage] = useState<string | null>(null);

  const guardPurchase = (action: () => void, message?: string, defaultAuthMode: 'login' | 'signup' = 'login') => {
    if (user) {
      try {
        action();
      } catch (err) {
        console.error('Error executing purchase action:', err);
      }
      return true;
    } else {
      localStorage.setItem('auth_modal_initial_mode', defaultAuthMode);
      setAuthModalModeState(defaultAuthMode);
      setPendingPurchaseAction({ fn: action });
      setPurchaseAuthMessage(message || '🔐 Login Required');
      setAuthModalOpenState(true);
      return false;
    }
  };

  const executePendingPurchaseAction = () => {
    if (pendingPurchaseAction && typeof pendingPurchaseAction.fn === 'function') {
      const actionToRun = pendingPurchaseAction.fn;
      setPendingPurchaseAction(null);
      setPurchaseAuthMessage(null);
      try {
        actionToRun();
      } catch (err) {
        console.error('Error in executePendingPurchaseAction:', err);
      }
    }
  };
  const [isPremiumIntelligenceEnabled, setPremiumIntelligenceEnabled] = useState(() => {
    return localStorage.getItem('mcq_pulse_premium_intelligence_enabled') === 'true';
  });
  
  useEffect(() => {
    localStorage.setItem('mcq_pulse_premium_intelligence_enabled', String(isPremiumIntelligenceEnabled));
  }, [isPremiumIntelligenceEnabled]);

  const [dashboardTab, setDashboardTab] = useState<'overview' | 'courses' | 'rewards' | 'premium-intelligence' | 'my-learning' | 'parent-report' | 'purchased-pdfs' | 'security' | 'academic-history'>('my-learning');
  const [adminWorkspace, setAdminWorkspace] = useState<'console' | 'studymaterials' | 'analytics' | 'business_analytics' | 'reset' | 'security' | 'admin_management' | 'cms' | 'practice_settings' | 'admission' | 'purchase_reports'>('business_analytics');

  useEffect(() => {
    if (user && user.role === 'student') {
      const userCartKey = `mcq_pulse_cart_${user.uid || user.email || 'student'}`;
      localStorage.setItem(userCartKey, JSON.stringify(cart));
      localStorage.setItem('mcq_pulse_cart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('mcq_pulse_cart');
    }
  }, [cart, user]);

  useEffect(() => {
    const handleUrlRouting = () => {
      const path = window.location.pathname;
      const hash = window.location.hash;
      if (path === '/admin' || path.startsWith('/admin/') || hash === '#admin') {
        setActivePage('admin-portal');
      }
    };
    handleUrlRouting();
    window.addEventListener('popstate', handleUrlRouting);
    return () => window.removeEventListener('popstate', handleUrlRouting);
  }, []);

  const [courses, setCourses] = useState<Course[]>(() => {
    const stored = localStorage.getItem('mcq_pulse_courses');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Course[];
        return parsed.map(c => {
          if (c.id === 'course_2' && c.category !== '📚 Academic Science') {
            const fresh = MOCK_COURSES.find(mc => mc.id === 'course_2');
            return fresh || c;
          }
          return c;
        });
      } catch (e) {
        return MOCK_COURSES;
      }
    }
    return MOCK_COURSES;
  });

  const [quizzes, setQuizzes] = useState<Quiz[]>(() => {
    const stored = localStorage.getItem('mcq_pulse_quizzes');
    if (stored) {
      try {
        const parsed: Quiz[] = JSON.parse(stored);
        return parsed.map(q => {
          const mockQ = MOCK_QUIZZES.find(mq => mq.id === q.id);
          return mockQ ? { ...q, ...mockQ } : q;
        });
      } catch (e) {
        return MOCK_QUIZZES;
      }
    }
    return MOCK_QUIZZES;
  });

  const [notifications, setNotifications] = useState<Notification[]>(() => {
    const stored = localStorage.getItem('mcq_pulse_notifications');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (_) {}
    }
    return [
      {
        id: 'n_welcome',
        title: 'Welcome to MCQ PULSE™!',
        message: 'Where every question sparks success. Start by exploring practise hub quizzes to ignite your learning streak!',
        date: new Date().toLocaleDateString(),
        read: false,
        type: 'announcement'
      }
    ];
  });

  const [testResults, setTestResults] = useState<TestResult[]>(() => {
    const stored = localStorage.getItem('mcq_pulse_results');
    if (stored) {
      try { return JSON.parse(stored); } catch (_) {}
    }
    return [];
  });

  const [certificates, setCertificates] = useState<Certificate[]>(() => {
    const stored = localStorage.getItem('mcq_pulse_certificates');
    if (stored) {
      try { return JSON.parse(stored); } catch (_) {}
    }
    return [];
  });

  const [errorReports, setErrorReports] = useState<ErrorReport[]>(() => {
    const stored = localStorage.getItem('mcq_pulse_error_reports');
    if (stored) {
      try { return JSON.parse(stored); } catch (_) {}
    }
    return [];
  });

  // Apply dark mode class to html (documentElement) and body elements
  useEffect(() => {
    const rootClass = document.documentElement.classList;
    const bodyClass = document.body.classList;
    if (theme === 'dark') {
      rootClass.add('dark');
      bodyClass.add('dark');
    } else {
      rootClass.remove('dark');
      bodyClass.remove('dark');
    }
    localStorage.setItem('mcq_pulse_theme', theme);
  }, [theme]);

  // Persist variables changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('mcq_pulse_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('mcq_pulse_user');
    }
  }, [user]);

  // Handle daily streak reset if a student misses a day
  useEffect(() => {
    if (user && user.lastPracticeDate && user.currentStreak > 0) {
      const todayStr = new Date().toISOString().split('T')[0];
      const lastDate = new Date(user.lastPracticeDate + 'T00:00:00Z');
      const todayDate = new Date(todayStr + 'T00:00:00Z');
      const diffTime = todayDate.getTime() - lastDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays > 1) {
        setUser(prev => {
          if (!prev) return null;
          return {
            ...prev,
            currentStreak: 0,
            dailyStreak: 0
          };
        });
      }
    }
  }, [user?.lastPracticeDate, user?.currentStreak]);

  useEffect(() => {
    localStorage.setItem('mcq_pulse_courses', JSON.stringify(courses));
  }, [courses]);

  useEffect(() => {
    localStorage.setItem('mcq_pulse_quizzes', JSON.stringify(quizzes));
  }, [quizzes]);

  useEffect(() => {
    localStorage.setItem('mcq_pulse_notifications', JSON.stringify(notifications));
  }, [notifications]);

  useEffect(() => {
    localStorage.setItem('mcq_pulse_results', JSON.stringify(testResults));
  }, [testResults]);

  useEffect(() => {
    localStorage.setItem('mcq_pulse_certificates', JSON.stringify(certificates));
  }, [certificates]);

  useEffect(() => {
    localStorage.setItem('mcq_pulse_error_reports', JSON.stringify(errorReports));
  }, [errorReports]);

  const changeTheme = (newTheme: 'light' | 'dark') => {
    setTheme(newTheme);
  };

  const setPage = (
    page: string,
    params?: { courseId?: string; lessonId?: string; quizId?: string; blogId?: string; profileTab?: 'view' | 'settings'; pdfClassId?: string; pdfSubjectId?: string; pdfClassName?: string; pdfSubjectName?: string }
  ) => {
    if (page === 'login') {
      setAuthModalOpen(true, 'login');
      return;
    }
    if (page === 'signup') {
      setAuthModalOpen(true, 'signup');
      return;
    }
    if (page === 'study-material' && !params?.pdfSubjectId && !params?.pdfClassId) {
      localStorage.removeItem('mp_selected_subject_id');
      window.dispatchEvent(new CustomEvent('reset_study_material_library'));
    }
    setActivePage(page);
    if (params) {
      if ('courseId' in params) setActiveCourseId(params.courseId || null);
      if ('lessonId' in params) setActiveLessonId(params.lessonId || null);
      if ('quizId' in params) setActiveQuizId(params.quizId || null);
      if ('blogId' in params) setSelectedBlogId(params.blogId || null);
      if ('profileTab' in params) setProfileTab(params.profileTab || 'view');
      if ('pdfClassId' in params) setPdfLibraryClassId(params.pdfClassId || null);
      if ('pdfSubjectId' in params) setPdfLibrarySubjectId(params.pdfSubjectId || null);
      if ('pdfClassName' in params) setPdfLibraryClassName(params.pdfClassName || null);
      if ('pdfSubjectName' in params) setPdfLibrarySubjectName(params.pdfSubjectName || null);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const login = async (
    email: string,
    role: UserRole,
    password?: string,
    captcha?: string,
    expectedCaptcha?: string,
    forceLogin?: boolean
  ): Promise<any> => {
    try {
      const visitorId = getOrCreateVisitorId();
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-visitor-id': visitorId
        },
        body: JSON.stringify({ email, password, role, captcha, expectedCaptcha, forceLogin, visitorId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const err = new Error(errorData.error || 'Login failed.');
        (err as any).isApiError = true;
        throw err;
      }

      const data = await response.json();

      if (data.alreadyActive) {
        return { alreadyActive: true, message: data.message };
      }
      
      // Save tokens
      if (data.accessToken) {
        localStorage.setItem('mcq_pulse_token', data.accessToken);
        localStorage.setItem('token', data.accessToken);
      }

      const rawUserRoleLower = (data.user?.role?.toLowerCase() || role || 'student');
      const normalizedRole: UserRole = (rawUserRoleLower === 'super admin' || rawUserRoleLower === 'admin') ? 'admin' : (rawUserRoleLower as UserRole);
      const adminRoleVal = rawUserRoleLower === 'super admin' ? 'Super Admin' : data.user?.adminRole;

      const loggedInUser: UserProfile = {
        uid: 'user_pulse_' + (data.user?.id || Math.floor(Math.random() * 1000)),
        email: data.user?.email || email,
        displayName: data.user?.display_name || email.split('@')[0].toUpperCase(),
        role: normalizedRole,
        adminRole: adminRoleVal,
        phone: data.user?.phone || '',
        dailyStreak: data.profile?.current_streak !== undefined ? Number(data.profile.current_streak) : 0,
        currentStreak: data.profile?.current_streak !== undefined ? Number(data.profile.current_streak) : 0,
        highestStreak: data.profile?.highest_streak !== undefined ? Number(data.profile.highest_streak) : 0,
        lastPracticeDate: data.profile?.last_practice_date || undefined,
        milestonesUnlocked: data.profile?.milestones_unlocked || [],
        lastActiveDate: data.profile?.last_active_date || new Date().toISOString().split('T')[0],
        xp: data.profile?.xp !== undefined ? Number(data.profile.xp) : 0,
        credits: data.profile?.credits !== undefined ? Number(data.profile.credits) : 0,
        badges: (data.profile?.badges && Array.isArray(data.profile.badges)) ? data.profile.badges : [],
        unlockedCertificates: [],
        isPremium: data.profile?.isPremium !== undefined 
          ? Boolean(data.profile.isPremium) 
          : (data.user?.isPremium !== undefined ? Boolean(data.user.isPremium) : false),
        enrolledCourses: (data.profile?.enrolledCourses && Array.isArray(data.profile.enrolledCourses))
          ? data.profile.enrolledCourses
          : [],
        completedQuizzes: [],
        xpHistory: [
          { date: '2026-07-03', xp: 100 },
          { date: '2026-07-04', xp: 200 },
          { date: '2026-07-05', xp: 350 },
          { date: '2026-07-06', xp: 450 }
        ],
        creditsSpent: 0,
        createdAt: new Date().toLocaleDateString(),
        currentClass: data.profile?.currentClass || data.profile?.grade_or_class || undefined,
        academicYear: role === 'student' ? '2026-2027' : undefined,
        photoURL: data.profile?.avatar || ''
      };

      const hydratedClass = user?.currentClass || 'None';
      console.log('[Sync] Hydrated localStorage currentClass:', hydratedClass);
      console.log('[Sync] API returned currentClass:', loggedInUser.currentClass);
      console.log('[Sync] Final user.currentClass after synchronization:', loggedInUser.currentClass);

      // Overwrite localStorage user cache completely
      // Clear practice history when a NEW user logs in to prevent data leakage from previous sessions
      if (!user || user.email !== loggedInUser.email) {
        localStorage.removeItem('mcq_pulse_premium_attempts');
        localStorage.removeItem('mcq_pulse_focus_topics');
        localStorage.removeItem('mcq_pulse_daily_practice_config');
        localStorage.removeItem('mcq_pulse_daily_leaderboard');
        localStorage.removeItem('mcq_pulse_premium_leaderboard');
        localStorage.removeItem('mcq_pulse_premium_last_result');
      }
      
      localStorage.setItem('mcq_pulse_user', JSON.stringify(loggedInUser));
      setUser(loggedInUser);
      return true;
    } catch (err: any) {
      // If server returned an API validation or processing error, throw it so UI displays it
      if (err.isApiError) {
        throw err;
      }

      console.warn('Backend server connection error, falling back to secure simulated sandbox auth:', err);
      // Fallback local mock user simulation
      const mockUser: UserProfile = {
        uid: 'user_pulse_' + Math.floor(Math.random() * 1000),
        email: email,
        displayName: email.split('@')[0].toUpperCase(),
        role: role,
        adminRole: role === 'admin' ? 'Super Admin' : undefined,
        dailyStreak: 0,
        currentStreak: 0,
        highestStreak: 0,
        lastPracticeDate: undefined,
        milestonesUnlocked: [],
        lastActiveDate: new Date().toISOString().split('T')[0],
        xp: 0,
        credits: 0,
        badges: [],
        unlockedCertificates: [],
        isPremium: false,
        enrolledCourses: [],
        completedQuizzes: [],
        xpHistory: [
          { date: '2026-07-03', xp: 100 },
          { date: '2026-07-04', xp: 200 },
          { date: '2026-07-05', xp: 350 },
          { date: '2026-07-06', xp: 450 }
        ],
        creditsSpent: 0,
        createdAt: new Date().toLocaleDateString(),
        currentClass: role === 'student' ? 'Class 6' : undefined,
        academicYear: role === 'student' ? '2026-2027' : undefined,
        phone: '+91 98765 43210',
        photoURL: ''
      };
      setUser(mockUser);
      return true;
    }
  };

  const loginWithGoogleData = async (data: any): Promise<boolean> => {
    if (!data) return false;
    try {
      if (data.accessToken) {
        localStorage.setItem('mcq_pulse_token', data.accessToken);
        localStorage.setItem('token', data.accessToken);
      }

      const rawUserRoleLower = (data.user?.role?.toLowerCase() || 'student');
      const normalizedRole: UserRole = (rawUserRoleLower === 'super admin' || rawUserRoleLower === 'admin') ? 'admin' : (rawUserRoleLower as UserRole);
      const adminRoleVal = rawUserRoleLower === 'super admin' ? 'Super Admin' : data.user?.adminRole;

      const loggedInUser: UserProfile = {
        uid: 'user_pulse_' + (data.user?.id || Math.floor(Math.random() * 1000)),
        email: data.user?.email || '',
        displayName: (data.user?.display_name && data.user?.display_name.trim().toLowerCase() !== 'student') ? data.user.display_name : '',
        role: normalizedRole,
        adminRole: adminRoleVal,
        phone: data.user?.phone || '',
        state: data.user?.state || undefined,
        dailyStreak: data.profile?.current_streak !== undefined ? Number(data.profile.current_streak) : 0,
        currentStreak: data.profile?.current_streak !== undefined ? Number(data.profile.current_streak) : 0,
        highestStreak: data.profile?.highest_streak !== undefined ? Number(data.profile.highest_streak) : 0,
        lastPracticeDate: data.profile?.last_practice_date || undefined,
        milestonesUnlocked: data.profile?.milestones_unlocked || [],
        lastActiveDate: data.profile?.last_active_date || new Date().toISOString().split('T')[0],
        xp: data.profile?.xp !== undefined ? Number(data.profile.xp) : 0,
        credits: data.profile?.credits !== undefined ? Number(data.profile.credits) : 0,
        badges: (data.profile?.badges && Array.isArray(data.profile.badges)) ? data.profile.badges : [],
        unlockedCertificates: [],
        isPremium: data.profile?.isPremium !== undefined 
          ? Boolean(data.profile.isPremium) 
          : (data.user?.isPremium !== undefined ? Boolean(data.user.isPremium) : false),
        enrolledCourses: (data.profile?.enrolledCourses && Array.isArray(data.profile.enrolledCourses))
          ? data.profile.enrolledCourses
          : [],
        completedQuizzes: [],
        xpHistory: [
          { date: '2026-07-03', xp: 100 },
          { date: '2026-07-04', xp: 200 },
          { date: '2026-07-05', xp: 350 },
          { date: '2026-07-06', xp: 450 }
        ],
        creditsSpent: 0,
        createdAt: new Date().toLocaleDateString(),
        currentClass: data.profile?.currentClass || data.profile?.grade_or_class || undefined,
        academicYear: normalizedRole === 'student' ? '2026-2027' : undefined,
        photoURL: data.user?.photoURL || data.profile?.avatar || ''
      };

      if (!user || user.email !== loggedInUser.email) {
        localStorage.removeItem('mcq_pulse_premium_attempts');
        localStorage.removeItem('mcq_pulse_focus_topics');
        localStorage.removeItem('mcq_pulse_daily_practice_config');
        localStorage.removeItem('mcq_pulse_daily_leaderboard');
        localStorage.removeItem('mcq_pulse_premium_leaderboard');
        localStorage.removeItem('mcq_pulse_premium_last_result');
      }

      localStorage.setItem('mcq_pulse_user', JSON.stringify(loggedInUser));
      setUser(loggedInUser);
      return true;
    } catch (err) {
      console.error('Error establishing session from Google OAuth:', err);
      return false;
    }
  };

  const signup = async (
    displayName: string,
    email: string,
    role: UserRole,
    currentClass?: string,
    password?: string,
    phone?: string,
    captcha?: string,
    expectedCaptcha?: string,
    userState?: string
  ): Promise<boolean> => {
    try {
      const visitorId = getOrCreateVisitorId();
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-visitor-id': visitorId
        },
        body: JSON.stringify({
          display_name: displayName,
          email,
          phone,
          password,
          role: role.charAt(0).toUpperCase() + role.slice(1), // Capitalize role for backend match
          currentClass,
          captcha,
          expectedCaptcha,
          visitorId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const err = new Error(errorData.error || 'Registration failed.');
        (err as any).errorCode = errorData.errorCode;
        (err as any).isApiError = true;
        throw err;
      }

      // Check if OTP is enabled on the backend
      const configRes = await fetch('/api/auth/config').catch(() => null);
      const configData = configRes && configRes.ok ? await configRes.json().catch(() => null) : null;
      const isOtpOn = configData ? configData.otpEnabled : false;

      if (!isOtpOn) {
        try {
          await login(email, role, password, undefined, undefined, false);
        } catch (e) {
          console.error("Auto login failed", e);
        }
        return true;
      }

      // Automatically log in the user upon successful signup
      const mockUser: UserProfile = {
        uid: 'user_pulse_' + Math.floor(Math.random() * 1000),
        email: email,
        displayName: displayName,
        role: role,
        dailyStreak: 1,
        currentStreak: 1,
        highestStreak: 1,
        lastPracticeDate: undefined,
        milestonesUnlocked: [],
        lastActiveDate: new Date().toISOString().split('T')[0],
        xp: 50,
        credits: 20,
        badges: [],
        unlockedCertificates: [],
        enrolledCourses: [],
        completedQuizzes: [],
        xpHistory: [
          { date: new Date().toISOString().split('T')[0], xp: 50 }
        ],
        creditsSpent: 0,
        createdAt: new Date().toLocaleDateString(),
        currentClass: role === 'student' ? (currentClass || 'Class 6') : undefined,
        state: userState || 'Delhi',
        academicYear: role === 'student' ? '2026-2027' : undefined,
        phone: phone || '',
        photoURL: ''
      };

      setUser(mockUser);
      if (role === 'student') {
        setPage('welcome');
      } else {
        setPage('login-loading');
      }
      addNotification('Welcome Onboard!', `Hey ${displayName}, your journey with MCQ PULSE™ starts today! Complete lessons to earn badges.`, 'announcement');
      return true;
    } catch (err: any) {
      if (err.isApiError) {
        throw err;
      }

      console.warn('Backend server connection error, falling back to secure simulated signup:', err);
      
      // Fallback
      // Check if OTP is enabled on the backend
      const configRes = await fetch('/api/auth/config').catch(() => null);
      const configData = configRes && configRes.ok ? await configRes.json().catch(() => null) : null;
      const isOtpOn = configData ? configData.otpEnabled : false;

      if (!isOtpOn) {
        return true;
      }

      const mockUser: UserProfile = {
        uid: 'user_pulse_' + Math.floor(Math.random() * 1000),
        email: email,
        displayName: displayName,
        role: role,
        dailyStreak: 1,
        currentStreak: 1,
        highestStreak: 1,
        lastPracticeDate: undefined,
        milestonesUnlocked: [],
        lastActiveDate: new Date().toISOString().split('T')[0],
        xp: 50,
        credits: 20,
        badges: [],
        unlockedCertificates: [],
        enrolledCourses: [],
        completedQuizzes: [],
        xpHistory: [
          { date: new Date().toISOString().split('T')[0], xp: 50 }
        ],
        creditsSpent: 0,
        createdAt: new Date().toLocaleDateString(),
        currentClass: role === 'student' ? (currentClass || 'Class 6') : undefined,
        state: userState || 'Delhi',
        academicYear: role === 'student' ? '2026-2027' : undefined,
        phone: phone || '',
        photoURL: ''
      };

      setUser(mockUser);
      if (role === 'student') {
        setPage('welcome');
      } else {
        setPage('login-loading');
      }
      addNotification('Welcome Onboard!', `Hey ${displayName}, your journey with MCQ PULSE™ starts today! Complete lessons to earn badges.`, 'announcement');
      return true;
    }
  };

  const logout = async () => {
    // 1. Destroy authenticated student session if token exists
    const studentToken = localStorage.getItem('token') || localStorage.getItem('mcq_pulse_token');
    if (studentToken) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${studentToken}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (err) {
        console.warn('Backend student logout request failed:', err);
      }
    }

    // 2. Destroy authenticated admin session via backend
    try {
      await fetch('/api/auth/admin-logout', { method: 'POST' });
    } catch (err) {
      console.warn('Backend admin-logout request failed:', err);
    }

    // 3. Clear all authentication cookies client-side
    const cookiesToClear = ['admin_session', 'session_id', 'csrf_token'];
    cookiesToClear.forEach(cookieName => {
      document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict;`;
    });

    // 4. Clear JWT/session tokens
    localStorage.removeItem('token');
    localStorage.removeItem('mcq_pulse_token');

    // 5. Clear any cached admin/student data
    localStorage.removeItem('mcq_pulse_user');
    
    // Clear local storage for practice history to ensure fresh state for new users
    localStorage.removeItem('mcq_pulse_premium_attempts');
    localStorage.removeItem('mcq_pulse_focus_topics');
    localStorage.removeItem('mcq_pulse_daily_practice_config');
    localStorage.removeItem('mcq_pulse_daily_leaderboard');
    localStorage.removeItem('mcq_pulse_premium_leaderboard');
    localStorage.removeItem('mcq_pulse_premium_last_result');

    // 6. Remove all sessionStorage values related to admin authentication
    try {
      sessionStorage.clear();
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }

    // 7. Set user state to null and clear cart
    setCart([]);
    localStorage.removeItem('mcq_pulse_cart');
    setUser(null);

    // 8. Redirect the user to the Website Home Page ("/") instead of the Admin Login page
    setPage('home');
  };

  const enrollInCourse = (courseId: string) => {
    if (!user) {
      setPage('login');
      return;
    }
    if (user.enrolledCourses.some(id => String(id) === String(courseId))) {
      setPage('course-details', { courseId });
      return;
    }

    const updatedCourses = [...user.enrolledCourses, String(courseId)];
    const awardXP = 100;
    const awardCredits = 20;

    // Check Badge triggers for Course Enrollment
    const updatedBadges = [...user.badges];
    if (!updatedBadges.includes('first_step')) {
      updatedBadges.push('first_step');
      setTimeout(() => {
        addNotification(
          '🏅 New Badge Unlocked!',
          'Congratulations! You unlocked the "First Step" badge for enrolling in your first course!',
          'badge'
        );
      }, 1000);
    }

    updateUserProfile({
      enrolledCourses: updatedCourses,
      xp: user.xp + awardXP,
      credits: user.credits + awardCredits,
      badges: updatedBadges,
      xpHistory: [...user.xpHistory, { date: new Date().toISOString().split('T')[0], xp: user.xp + awardXP }]
    });

    addNotification(
      'Enrolled Successfully',
      `You have enrolled in ${courses.find(c => c.id === courseId)?.title}. Earned +${awardXP} XP and +${awardCredits} Pulse Credits!`,
      'announcement'
    );
  };

  const completeLesson = (courseId: string, lessonId: string) => {
    if (!user) return;
    const course = courses.find(c => c.id === courseId);
    if (!course) return;

    // Award minor XP for watching lesson
    const rewardXP = 20;
    const rewardCredits = 5;

    // Check if course is fully completed to award certificate
    const completedInCourse = user.completedQuizzes.filter(qid => {
      const q = quizzes.find(quiz => quiz.id === qid);
      return q && q.category === course.category;
    });

    // Simple simulated rule: if they watched any lesson, give them progress
    updateUserProfile({
      xp: user.xp + rewardXP,
      credits: user.credits + rewardCredits,
      xpHistory: [...user.xpHistory, { date: new Date().toISOString().split('T')[0], xp: user.xp + rewardXP }]
    });

    addNotification(
      'Lesson Completed',
      `You completed ${course.lessons.find(l => l.id === lessonId)?.title}! Earned +${rewardXP} XP.`,
      'reminder'
    );

    // If it's the last lesson, let's offer to issue a Certificate!
    const isLastLesson = course.lessons[course.lessons.length - 1].id === lessonId;
    if (isLastLesson && !user.unlockedCertificates.includes(courseId)) {
      const newCert: Certificate = {
        id: 'cert_' + Math.floor(Math.random() * 100000),
        userId: user.uid,
        userName: user.displayName,
        courseId: course.id,
        courseName: course.title,
        instructorName: course.instructorName,
        issueDate: new Date().toLocaleDateString(),
        credentialId: 'MP-' + Math.random().toString(36).substring(2, 10).toUpperCase()
      };

      setCertificates(prev => [newCert, ...prev]);
      updateUserProfile({
        unlockedCertificates: [...user.unlockedCertificates, courseId]
      });

      addNotification(
        '🎓 Certificate Issued!',
        `Incredible work! You have completed "${course.title}" and earned your official certificate!`,
        'badge'
      );
    }
  };

  const submitQuizScore = (quizId: string, score: number, answers: any[], timeTakenSeconds: number): TestResult => {
    const quiz = quizzes.find(q => q.id === quizId);
    if (!quiz) throw new Error('Quiz not found');

    const totalQs = quiz.questions.length;
    const pct = score / totalQs;

    // Dynamic XP/Credits awards based on accuracy
    const calculatedXP = Math.floor(quiz.xpAward * pct);
    const calculatedCredits = Math.floor(quiz.creditAward * pct);

    const result: TestResult = {
      id: 'res_' + Math.floor(Math.random() * 1000000),
      quizId,
      quizTitle: quiz.title,
      quizType: quiz.type,
      userId: user?.uid || 'guest_user',
      score,
      totalQuestions: totalQs,
      xpEarned: calculatedXP,
      creditsEarned: calculatedCredits,
      date: new Date().toLocaleString(),
      timeTakenSeconds,
      answers
    };

    setTestResults(prev => [result, ...prev]);
    setLastResult(result);

    if (user) {
      const updatedQuizList = user.completedQuizzes.includes(quizId)
        ? user.completedQuizzes
        : [...user.completedQuizzes, quizId];

      // Check for Streak & Century Club triggers
      const updatedBadges = [...user.badges];
      let unlockedNewBadge = false;

      if (calculatedXP >= 100 && !updatedBadges.includes('quiz_master')) {
        updatedBadges.push('quiz_master');
        unlockedNewBadge = true;
      }

      const currentTotalXP = user.xp + calculatedXP;
      if (currentTotalXP >= 500 && !updatedBadges.includes('xp_level_1')) {
        updatedBadges.push('xp_level_1');
        unlockedNewBadge = true;
      }
      if (currentTotalXP >= 1500 && !updatedBadges.includes('xp_level_2')) {
        updatedBadges.push('xp_level_2');
        unlockedNewBadge = true;
      }

      // Automatically advance daily streak on completing quizzes
      const todayStr = new Date().toISOString().split('T')[0];
      let newStreak = user.dailyStreak;
      if (user.lastActiveDate !== todayStr) {
        newStreak = user.dailyStreak + 1;
        
        // Streak badges
        if (newStreak >= 3 && !updatedBadges.includes('streak_3')) {
          updatedBadges.push('streak_3');
          unlockedNewBadge = true;
        }
        if (newStreak >= 7 && !updatedBadges.includes('streak_7')) {
          updatedBadges.push('streak_7');
          unlockedNewBadge = true;
        }
      }

      updateUserProfile({
        completedQuizzes: updatedQuizList,
        xp: currentTotalXP,
        credits: user.credits + calculatedCredits,
        badges: updatedBadges,
        dailyStreak: newStreak,
        lastActiveDate: todayStr,
        xpHistory: [...user.xpHistory, { date: todayStr, xp: currentTotalXP }]
      });

      addNotification(
        'Quiz Finished!',
        `You scored ${score}/${totalQs} in ${quiz.title}. Earned +${calculatedXP} XP and +${calculatedCredits} Credits!`,
        'reminder'
      );

      if (unlockedNewBadge) {
        setTimeout(() => {
          addNotification(
            '🏅 Achievement Earned!',
            'You just unlocked a brand new badge! Inspect your profile card to celebrate.',
            'badge'
          );
        }, 1500);
      }
    }

    return result;
  };

  const updateUserProfile = (fields: Partial<UserProfile>) => {
    if (!user) return;
    setUser(prev => {
      if (!prev) return null;
      return { ...prev, ...fields };
    });
  };

  const isProfileIncomplete = (u: any) => {
    if (!u) return false;
    if (u.role !== 'student') return false;
    const name = u.displayName ? u.displayName.trim() : '';
    const currentClass = u.currentClass ? u.currentClass.trim() : '';
    const state = u.state ? u.state.trim() : '';
    const phone = u.phone ? u.phone.trim() : '';
    if (!name || name === 'Student' || name.toLowerCase() === 'student') return true;
    if (!currentClass) return true;
    if (!state) return true;
    if (!phone) return true;
    return false;
  };

  const addToCart = (courseId: string) => {
    guardPurchase(() => {
      const courseLookup: { [key: string]: { id: string; title: string; image: string; cardImage?: string; heroImage?: string; price: number } } = {
        '6-sci': { id: '6-sci', title: 'NCERT Class 6 Science', image: '/images/courses/c6s1.webp', price: 149 },
        '6-math': { id: '6-math', title: 'NCERT Class 6 Mathematics', image: '/images/courses/c6m1.webp', price: 149 },
        '6-social': { id: '6-social', title: 'NCERT Class 6 Social Science', image: '/images/courses/c6ss1.webp', price: 149 },
        '7-sci': { id: '7-sci', title: 'NCERT Class 7 Science', image: '/images/courses/c7s1.webp', cardImage: '/images/courses/c7s1.webp', heroImage: '/images/courses/c7s1.webp', price: 149 },
        '7-math': { id: '7-math', title: 'NCERT Class 7 Mathematics', image: '/images/courses/c7m1.webp', cardImage: '/images/courses/c7m1.webp', heroImage: '/images/courses/c7m1.webp', price: 149 },
        '7-social': { id: '7-social', title: 'NCERT Class 7 Social Science', image: '/images/courses/c7ss1.webp', cardImage: '/images/courses/c7ss1.webp', heroImage: '/images/courses/c7ss1.webp', price: 149 },
        '8-sci': { id: '8-sci', title: 'NCERT Class 8 Science', image: '/images/courses/c8s1.webp', price: 149 },
        '8-math': { id: '8-math', title: 'NCERT Class 8 Mathematics', image: '/images/courses/c8m1.webp', price: 149 },
        '8-social': { id: '8-social', title: 'NCERT Class 8 Social Science', image: '/images/courses/c8ss1.webp', price: 149 },
      };

    // Use dynamic price from promotions plan if available
    let resolvedPrice = 149;
    const isLaunchOfferActive = () => {
      if (!promotions) return false;
      if (promotions.free_premium_active !== 'true') return false;
      const now = new Date();
      if (promotions.free_premium_start_date) {
        const start = new Date(promotions.free_premium_start_date);
        if (now < start) return false;
      }
      if (promotions.free_premium_end_date) {
        const end = new Date(promotions.free_premium_end_date);
        if (now > end) return false;
      }
      return true;
    };

    if (isLaunchOfferActive()) {
      resolvedPrice = 0;
    } else if (promotions && promotions.membership_plans) {
      const plan = promotions.membership_plans.find((p: any) => p.id === courseId);
      if (plan) {
        resolvedPrice = Number(plan.sellingPrice);
      }
    } else {
      const baseObj = courseLookup[courseId];
      if (baseObj) {
        resolvedPrice = baseObj.price;
      }
    }

    const baseInfo = courseLookup[courseId] || {
      id: courseId,
      title: courseId.replace('-', ' ').toUpperCase(),
      image: '/images/courses/c6s1.webp',
      price: resolvedPrice
    };

    const courseInfo = {
      ...baseInfo,
      price: resolvedPrice
    };

    setCart(prev => {
      const exists = prev.some(item => item.id === courseId);
      if (exists) {
        return prev; // No duplicate courses in the cart
      }
      return [...prev, courseInfo];
    });

      if (isProfileIncomplete(user)) {
        setPage('cart');
      } else {
        setCartModalItem(courseInfo);
        setCartModalOpen(true);
      }
    }, 'Please Login to Continue');
  };

  const addPdfToCart = (chapter: any, price: number, className: string, subjectName: string) => {
    guardPurchase(() => {
      const pdfItem = {
        id: `pdf-${chapter.id}`,
        chapter_id: chapter.id,
        item_type: 'pdf',
        title: `Ch ${chapter.chapterNumber || chapter.chapter_number || ''} PDF - ${chapter.chapterName || chapter.chapter_name || ''}`,
        subtitle: `${className} • ${subjectName}`,
        image: '/images/courses/c6s1.jpg',
        price: price
      };

      setCart(prev => {
        const exists = prev.some(item => item.id === pdfItem.id);
        if (exists) {
          return prev;
        }
        return [...prev, pdfItem];
      });

      if (isProfileIncomplete(user)) {
        setPage('cart');
      } else {
        setCartModalItem(pdfItem);
        setCartModalOpen(true);
      }
    }, 'Please Login to Continue');
  };

  const removeFromCart = (courseId: string) => {
    setCart(prev => prev.filter(item => item.id !== courseId));
  };

  const clearCart = () => {
    setCart([]);
  };

  const addNotification = (
    title: string,
    message: string,
    type: 'streak' | 'badge' | 'announcement' | 'reminder'
  ) => {
    const newNotification: Notification = {
      id: 'n_' + Math.floor(Math.random() * 1000000),
      title,
      message,
      date: new Date().toLocaleDateString(),
      read: false,
      type
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const clearNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const purchaseItem = (cost: number, itemName: string, itemType: 'streak-freeze' | 'avatar' | 'premium'): boolean => {
    if (!user) return false;
    if (user.credits < cost) {
      addNotification('Transaction Declined', 'Inadequate Pulse Credits. Solve more challenges to earn currency!', 'reminder');
      return false;
    }

    // Process Purchase
    const updatedCredits = user.credits - cost;
    const spentAmount = user.creditsSpent + cost;

    updateUserProfile({
      credits: updatedCredits,
      creditsSpent: spentAmount
    });

    addNotification(
      'Purchase Confirmed!',
      `You spent ${cost} Credits to acquire: ${itemName}. Enjoy your reward!`,
      'announcement'
    );

    return true;
  };

  // Admin / Teacher Operations
  const createCourse = (newCourse: Omit<Course, 'id'>) => {
    const withId: Course = { ...newCourse, id: 'course_' + Math.floor(Math.random() * 100000) };
    setCourses(prev => [withId, ...prev]);
    addNotification('New Course Created', `Course "${newCourse.title}" is now live on MCQ Pulse™!`, 'announcement');
  };

  const updateCourse = (id: string, updatedFields: Partial<Course>) => {
    setCourses(prev => prev.map(c => c.id === id ? { ...c, ...updatedFields } as Course : c));
  };

  const deleteCourse = (id: string) => {
    setCourses(prev => prev.filter(c => c.id !== id));
    addNotification('Course Archived', 'The selected course has been successfully archived.', 'announcement');
  };

  const createQuiz = (newQuiz: Omit<Quiz, 'id'>) => {
    const withId: Quiz = { ...newQuiz, id: 'quiz_' + Math.floor(Math.random() * 100000) };
    setQuizzes(prev => [withId, ...prev]);
    addNotification('New Quiz Added', `Quiz "${newQuiz.title}" has been successfully added to the Practise Hub!`, 'announcement');
  };

  const updateQuiz = (id: string, updatedFields: Partial<Quiz>) => {
    setQuizzes(prev => prev.map(q => q.id === id ? { ...q, ...updatedFields } as Quiz : q));
  };

  const deleteQuiz = (id: string) => {
    setQuizzes(prev => prev.filter(q => q.id !== id));
    addNotification('Quiz Deleted', 'The requested quiz was removed from active listings.', 'announcement');
  };

  const reportError = (newReport: Omit<ErrorReport, 'id' | 'date' | 'status'>) => {
    const report: ErrorReport = {
      ...newReport,
      id: 'rep_' + Math.floor(Math.random() * 1000000),
      date: new Date().toLocaleDateString(),
      status: 'pending'
    };
    setErrorReports(prev => [report, ...prev]);
    addNotification(
      'Report Submitted',
      `Thank you for helping us maintain accuracy! Your error report was dispatched to the Admin Review Queue.`,
      'reminder'
    );
  };

  const resolveErrorReport = (id: string, adminNotes?: string) => {
    setErrorReports(prev => prev.map(r => r.id === id ? { ...r, status: 'resolved', adminNotes } : r));
    addNotification('Report Resolved', 'The selected error report has been updated and marked as resolved.', 'announcement');
  };

  const updateQuestionContent = (quizId: string, questionId: string, text: string, options: string[], explanation: string) => {
    setQuizzes(prev => prev.map(q => {
      if (q.id === quizId) {
        return {
          ...q,
          questions: q.questions.map(question => {
            if (question.id === questionId) {
              return { ...question, text, options, explanation };
            }
            return question;
          })
        };
      }
      return q;
    }));
    addNotification('Content Updated', 'The MCQ question content and explanation were successfully corrected.', 'announcement');
  };

  const updateLessonNotes = (courseId: string, lessonId: string, notes: string) => {
    setCourses(prev => prev.map(c => {
      if (c.id === courseId) {
        return {
          ...c,
          lessons: c.lessons.map(l => {
            if (l.id === lessonId) {
              return { ...l, notes };
            }
            return l;
          })
        };
      }
      return c;
    }));
    addNotification('Syllabus Updated', 'The chapter textbook summary notes were successfully corrected.', 'announcement');
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get('verified');
    const verificationError = urlParams.get('verification_error');

    if (verified === 'true') {
      // Clear query parameters from URL without page reload
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      setAuthModalOpen(true);
      addNotification(
        'Email Verified 🎉',
        'Your student account was verified successfully! You can now log in.',
        'announcement'
      );
    } else if (verificationError) {
      // Clear query parameters from URL without page reload
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      setAuthModalOpen(true);
      addNotification(
        'Verification Alert ⚠️',
        decodeURIComponent(verificationError),
        'reminder'
      );
    }
  }, []);

  if (isProfileSyncing && (localStorage.getItem('mcq_pulse_token') || localStorage.getItem('token'))) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4">
        <div className="flex flex-col items-center space-y-4 max-w-sm text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-700 dark:text-slate-200 text-sm font-semibold">Synchronizing student profile...</p>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        activePage,
        activeCourseId,
        activeLessonId,
        activeQuizId,
        lastResult,
        selectedBlogId,
        courses,
        quizzes,
        notifications,
        theme,
        pdfLibraryClassId,
        pdfLibrarySubjectId,
        pdfLibraryClassName,
        pdfLibrarySubjectName,
        setPage,
        login,
        loginWithGoogleData,
        signup,
        logout,
        enrollInCourse,
        completeLesson,
        submitQuizScore,
        addNotification,
        clearNotification,
        purchaseItem,
        changeTheme,
        createCourse,
        updateCourse,
        deleteCourse,
        createQuiz,
        updateQuiz,
        deleteQuiz,
        testResults,
        badges: MOCK_BADGES,
        certificates,
        errorReports,
        reportError,
        resolveErrorReport,
        updateQuestionContent,
        updateLessonNotes,
        updateUserProfile,
        syncUserProfile,
        isProfileIncomplete,
        cart,
        addToCart,
        addPdfToCart,
        removeFromCart,
        clearCart,
        cartModalOpen,
        setCartModalOpen,
        cartModalItem,
        authModalOpen,
        authModalMode,
        setAuthModalOpen,
        setAuthModalMode,
        adminLoginModalOpen,
        setAdminLoginModalOpen,
        pendingPurchaseAction,
        setPendingPurchaseAction,
        purchaseAuthMessage,
        setPurchaseAuthMessage,
        guardPurchase,
        executePendingPurchaseAction,
        profileTab,
        setProfileTab,
        isPremiumIntelligenceEnabled,
        setPremiumIntelligenceEnabled,
        dashboardTab,
        setDashboardTab,
        adminWorkspace,
        setAdminWorkspace,
        promotions,
        refreshPromotions,
        activateFreePremiumOnServer,
        promoteStudent
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used inside an AppProvider');
  }
  return context;
};
